import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Gem, Coins, Backpack, Plus, ArrowRight, CircleHelp, Cable } from "lucide-react";
import { Button } from "./ui/button";
import { BadgePill } from "./ProductCard";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { useLang } from "../i18n/LanguageContext";
import { formatEUR } from "../lib/api";

const ROLE_META = {
  best_match: { key: "advisor.bestMatch", icon: Sparkles, tone: "cyan" },
  best_value: { key: "advisor.bestValue", icon: Coins, tone: "mint" },
  premium: { key: "advisor.premium", icon: Gem, tone: "amber" },
  portable: { key: "advisor.portable", icon: Backpack, tone: "cyan" },
};
const border = { cyan: "shadow-glow-cyan", mint: "shadow-glow-mint", amber: "shadow-glow-amber" };

const RecCard = ({ role, rec }) => {
  const { t } = useLang();
  const { addToCart, toggleCompare } = useStore();
  const meta = ROLE_META[role] || ROLE_META.best_match;
  const p = rec.product;
  if (!p) return null;
  const add = () => { addToCart({ slug: p.slug, name: p.name, price_eur: p.price_eur, image: p.image, kind: "product" }); toast.success(`${p.name} \u2014 ${t("common.added")}`); };
  return (
    <div data-testid="advisor-recommendation-card" className={`rounded-2xl card-ceramic p-4 ${border[meta.tone]}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg hairline"><meta.icon className="h-4 w-4 text-cyanx" /></span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{t(meta.key)}</span>
      </div>
      <div className="flex gap-3">
        <Link to={`/product/${p.slug}`} className="h-20 w-20 shrink-0 rounded-lg bg-black/40 overflow-hidden">
          <img src={p.image} alt={p.name} className="h-full w-full object-contain p-1.5" />
        </Link>
        <div className="min-w-0">
          <Link to={`/product/${p.slug}`}><h4 className="font-display leading-tight hover:text-cyanx transition-colors">{p.name}</h4></Link>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="font-display text-lg">{formatEUR(p.price_eur)}</span>
            {p.compare_at_eur && <span className="text-xs text-muted-foreground line-through">{formatEUR(p.compare_at_eur)}</span>}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">{(p.badges || []).slice(0,2).map((b) => <BadgePill key={b} label={b} />)}</div>
        </div>
      </div>
      {rec.why?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {rec.why.slice(0, 3).map((w, i) => (
            <li key={i} className="text-xs text-foreground/75 flex gap-2"><span className="text-cyanx mt-0.5">✓</span><span>{w}</span></li>
          ))}
        </ul>
      )}
      {rec.fit_notes && <p className="mt-2 text-xs text-muted-foreground italic">{rec.fit_notes}</p>}
      <div className="mt-3 flex gap-2">
        <Button onClick={add} size="sm" className="btn-glow border-0 flex-1"><Plus className="h-4 w-4 mr-1" />{t("common.addToCart")}</Button>
        <Button onClick={() => { toggleCompare(p.slug); toast(t("nav.compare")); }} size="sm" variant="secondary" className="hairline">{t("common.compareAdd")}</Button>
      </div>
    </div>
  );
};

export const AdvisorRecommendations = ({ result }) => {
  const { t } = useLang();
  if (!result) return (
    <div className="h-full flex items-center justify-center text-center p-8">
      <p className="text-muted-foreground text-sm">{t("advisor.noPicks")}</p>
    </div>
  );
  const recs = result.recommendations || {};
  const order = ["best_match", "best_value", "premium", "portable"];
  return (
    <div className="space-y-4">
      {result.summary && (
        <div className="rounded-xl hairline bg-cyanx/[0.04] p-4">
          <p className="text-sm text-foreground/85">{result.summary}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {order.filter((r) => recs[r]).map((r) => <RecCard key={r} role={r} rec={recs[r]} />)}
      </div>
      {result.accessories?.length > 0 && (
        <div className="rounded-xl card-ceramic p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Cable className="h-4 w-4 text-cyanx" />{t("advisor.suggestedAcc")}</p>
          <div className="flex flex-wrap gap-2">
            {result.accessories.map((a, i) => (
              <Link to="/accessories" key={i} className="chip hover:shadow-glow-cyan transition-shadow">
                {a.name || a.type}{a.price_eur ? ` \u00b7 ${formatEUR(a.price_eur)}` : ""}
              </Link>
            ))}
          </div>
        </div>
      )}
      {result.next_questions?.length > 0 && (
        <div className="rounded-xl hairline p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><CircleHelp className="h-4 w-4" />{t("advisor.nextQ")}</p>
          <ul className="space-y-1">{result.next_questions.map((q, i) => <li key={i} className="text-xs text-foreground/70">• {q}</li>)}</ul>
        </div>
      )}
      <Button asChild variant="secondary" className="w-full hairline"><Link to="/compare">{t("nav.compare")} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
    </div>
  );
};
